import { Button } from '@/ui/button'
import * as React from 'react'
import { type ReactNode, useEffect, useState } from 'react'
import { hasAuthParams, useAuth } from 'react-oidc-context'
import { Alert } from './Alert'
import { AuthLayout } from './app/layouts/AuthLayout'
import { Loader } from './ui'
import { UserProvider } from './modules/shared/context/UserContext'
import { OpenReplayTracker } from './modules/shared/components/OpenReplayTracker'
import { MaintenanceGuard } from './modules/shared/components/MaintenanceGuard'
import { currentUrlState } from './config'

interface ProtectedAppProps {
  children: ReactNode
}

export const ProtectedApp = (props: ProtectedAppProps) => {
  const { children } = props

  const auth = useAuth()
  const [hasTriedSignin, setHasTriedSignin] = useState(false)

  /**
   * Do auto sign in.
   *
   * See {@link https://github.com/authts/react-oidc-context?tab=readme-ov-file#automatic-sign-in}
   */
  useEffect(() => {
    if (
      !(
        hasAuthParams() ||
        auth.isAuthenticated ||
        auth.activeNavigator ||
        auth.isLoading ||
        hasTriedSignin
      )
    ) {
      // url_state → po zalogowaniu /postLogin wróci na tę stronę (deep link).
      auth.signinRedirect({ url_state: currentUrlState() })
      setHasTriedSignin(true)
    }
  }, [auth, hasTriedSignin])

  if (auth.isLoading) {
    return <Loader className="tw-h-screen tw-w-screen" />
  }
  if (auth.error) {
    // Błąd inicjalizacji OIDC lub nieudana wymiana kodu na token (np. zużyty
    // ?code= po przywróceniu karty). Świeża runda logowania zamiast martwego
    // ekranu / automatycznej pętli redirectów.
    return (
      <AuthLayout>
        <Alert variant="error">Błąd autoryzacji</Alert>
        <Alert variant="error">{auth.error.message || 'Błąd podczas autoryzacji'}</Alert>
        <Button
          type="button"
          variant="primary"
          className="tw-mt-4"
          onClick={() => auth.signinRedirect()}
        >
          Zaloguj ponownie
        </Button>
      </AuthLayout>
    )
  }
  if (!auth.isAuthenticated) {
    // Zwykle trwa właśnie redirect na Keycloak (efekt wyżej) — pokaż loader,
    // przeglądarka za moment opuści stronę.
    if (!hasTriedSignin) {
      return <Loader className="tw-h-screen tw-w-screen" />
    }
    return (
      <AuthLayout>
        <Alert variant="error">Błąd autoryzacji</Alert>
        <Alert variant="error">Nie można zalogować się</Alert>
      </AuthLayout>
    )
  }

  return (
    <UserProvider>
      <OpenReplayTracker />
      <MaintenanceGuard>
        {children}
      </MaintenanceGuard>
    </UserProvider>
  )
}
